import React from 'react';
import { useTranslation } from 'react-i18next';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Toaster } from 'sonner';

import SkillSearch from './SkillSearch';
import { calculateAdjustedScore } from '../../../Calculations/SkillScoreCalculator';
import { calculateSkillCost } from '../../../Calculations/SkillCostCalculator';
import useIsMobile from '../../../../hooks/useIsMobile';

import SkillsPanelDesktopRow from './desktop/SkillsPanel_DesktopRow';
import SkillsPanelMobileCard from './mobile/SkillsPanel_MobileCard';

const SkillsPanel = ({
  activeSkills,
  onToggleSkill,
  onLevelChange,
  onRemoveSkill,
  allSkills,
  onAddSkill,
  onUpgradeSkill,
  onToggleFastLearner,
  aptitudes,
  efficiencyRankings,
  optimalSkillIds,
  isLimiterActive,
  onToggleIgnoreCost,
  startOrder = 0 // Default to 0 if not provided
}) => {
  const { t } = useTranslation();
  const [hoveredUpgradeId, setHoveredUpgradeId] = React.useState(null);
  const isMobile = useIsMobile();
  // removed isThemeChanging

  // Clear hover state when active skills change to prevent stale tooltips
  React.useEffect(() => {
    setHoveredUpgradeId(null);
  }, [activeSkills]);

  // Stabilize getIconUrl to prevent child re-renders
  const getIconUrl = React.useCallback((iconId) => {
    if (!iconId) return null;
    return `/images/Icon/Skill_Icons/icon_skill_${iconId}.png`;
  }, []);

  // Group and sort skills: 
  // 1. Sort by Id_skill (Game ID) to establish the primary order.
  // 2. Apply Evolution Grouping: Upgrade above Base, anchored to Base position.
  const sortedSkills = React.useMemo(() => {
    if (!activeSkills || activeSkills.length === 0) return [];

    // Create a map for quick lookup
    const skillMap = new Map(activeSkills.map(item => [parseInt(item.skill.Id_skill, 10), item]));
    const processed = new Set();
    const result = [];

    // 1. Sort the pool by Id_skill (User Request: "ordenarse por su Id_skill")
    // This overrides the original insertion order (Innate -> Awakening -> Manual).
    // 1. Sort the pool: "Inherited Skills" first (by ID), then others (by ID)
    // User Request: "Inherited Skills... al principio"
    const sortedPool = [...activeSkills].sort((a, b) => {
      const isInheritedA = a.skill.color === 'Inherited Skills';
      const isInheritedB = b.skill.color === 'Inherited Skills';

      // If one is Inherited and the other is not, Inherited wins (comes first)
      if (isInheritedA && !isInheritedB) return -1;
      if (!isInheritedA && isInheritedB) return 1;

      // If both are same group, sort by ID
      const idA = parseInt(a.skill.Id_skill || 0, 10);
      const idB = parseInt(b.skill.Id_skill || 0, 10);
      return idA - idB;
    });

    // Helper to find all upgrades recursively
    const findUpgrades = (baseId) => {
      const upgrades = [];
      let currentBaseId = baseId;

      let foundNext = true;
      let safetyCounter = 0; // Prevent infinite loops
      while (foundNext && safetyCounter < 20) {
        // Create a local variable for the closure to capture safely
        const targetBaseId = currentBaseId;

        const upgradeItem = activeSkills.find(s =>
          s.skill.base_skill_id && parseInt(s.skill.base_skill_id, 10) === parseInt(targetBaseId, 10)
        );

        if (upgradeItem && !processed.has(parseInt(upgradeItem.skill.Id_skill, 10))) {
          upgrades.unshift(upgradeItem); // Add to front (Upgrade 2 above Upgrade 1)
          processed.add(parseInt(upgradeItem.skill.Id_skill, 10));
          currentBaseId = upgradeItem.skill.Id_skill;
          safetyCounter++;
        } else {
          foundNext = false;
        }
      }
      return upgrades;
    };

    // Iterate through the SORTED pool
    sortedPool.forEach(item => {
      const skillId = parseInt(item.skill.Id_skill, 10);

      if (processed.has(skillId)) return;

      // Check if this skill is an upgrade of another ACTIVE skill
      // If so, we skip it now, because we want to render it when we encounter its Base.
      if (item.skill.base_skill_id && skillMap.has(parseInt(item.skill.base_skill_id, 10))) {
        return;
      }

      // This is a Base (or independent skill).
      // 1. Find its upgrades (recursively)
      const upgrades = findUpgrades(skillId);

      // 2. Add upgrades first (so they appear above)
      result.push(...upgrades);

      // 3. Add the base itself
      result.push(item);
      processed.add(skillId);
    });

    return result;
  }, [activeSkills]);


  // --- RENDERING STRATEGY ---
  // Render all sorted skills immediately to prevent sync issues and flickering
  const visibleSkills = sortedSkills;

  // Máximo score entre skills activas — alimenta la SkillScoreBar D3
  const maxScore = React.useMemo(() => {
    if (!sortedSkills || sortedSkills.length === 0) return 0;
    return sortedSkills.reduce((max, item) => {
      const { adjustedScore } = calculateAdjustedScore(item.skill, aptitudes);
      return Math.max(max, adjustedScore || 0);
    }, 0);
  }, [sortedSkills, aptitudes]);
  // -----------------------------------

  return (
    <TooltipPrimitive.Provider delayDuration={400} skipDelayDuration={200}>
      {/* Toaster de sonner — una sola instancia en el panel */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'rgb(31 41 55)',
            color: 'rgb(243 244 246)',
            border: '1px solid rgb(55 65 81)',
            borderRadius: '12px',
            fontSize: '13px',
          },
        }}
      />

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{t('SkillsPanel.title_configuration')}</h3>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {t('SkillsPanel.subtitle_total', { count: activeSkills.length })}
          </div>
        </div>

        <SkillSearch allSkills={allSkills} onAddSkill={onAddSkill} activeSkills={activeSkills} />

        <div className="overflow-x-auto mt-6">
          {/* DESKTOP VIEW */}
          {!isMobile && (
            <table className="w-full text-left border-collapse min-w-0 md:min-w-[600px]">
              <thead className="hidden md:table-header-group">
                <tr className="text-left text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700 text-xs uppercase tracking-wider font-semibold">
                  <th className="pb-3 px-2 text-center w-12">{t('SkillsPanel.header_best')}</th>
                  <th className="pb-3 px-3 text-center w-14">{t('SkillsPanel.header_active')}</th>
                  <th className="pb-3 px-4">{t('SkillsPanel.header_details')}</th>
                  <th className="pb-3 px-2 text-center w-28">{t('SkillsPanel.header_options')}</th>
                  <th className="pb-3 px-2 text-center w-28">{t('SkillsPanel.header_level')}</th>
                  <th className="pb-3 px-2 text-right w-24">{t('SkillsPanel.header_cost')}</th>
                  <th className="pb-3 px-3 text-right w-28">{t('SkillsPanel.header_score')}</th>
                  <th className="pb-3 px-2 text-center w-24">{t('SkillsPanel.header_action')}</th>
                </tr>
              </thead>
              <tbody className="text-base block md:table-row-group">
                {visibleSkills.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center text-gray-400 dark:text-gray-500 italic text-base">
                      {t('SkillsPanel.msg_no_selection')}
                    </td>
                  </tr>
                ) : (
                  visibleSkills.map((item, index) => {
                    const props = getCommonProps(
                      item,
                      activeSkills,
                      aptitudes,
                      efficiencyRankings,
                      optimalSkillIds,
                      isLimiterActive,
                      getIconUrl,
                      setHoveredUpgradeId,
                      hoveredUpgradeId,
                      onToggleSkill,
                      allSkills,
                      onToggleFastLearner,
                      onLevelChange,
                      onToggleIgnoreCost,
                      onUpgradeSkill,
                      onRemoveSkill
                    );

                    return <SkillsPanelDesktopRow key={item.skill.Id} {...props} rowIndex={index} maxScore={maxScore} />;
                  })
                )}
              </tbody>
            </table>
          )}

          {isMobile && (
            <div className="flex flex-col gap-4">
              {visibleSkills.length === 0 ? (
                <div className="py-12 text-center text-gray-400 dark:text-gray-500 italic text-base">
                  {t('SkillsPanel.msg_no_selection')}
                </div>
              ) : (
                visibleSkills.map((item, index) => {
                  const props = getCommonProps(
                    item,
                    activeSkills,
                    aptitudes,
                    efficiencyRankings,
                    optimalSkillIds,
                    isLimiterActive,
                    getIconUrl,
                    setHoveredUpgradeId,
                    hoveredUpgradeId,
                    onToggleSkill,
                    allSkills,
                    onToggleFastLearner,
                    onLevelChange,
                    onToggleIgnoreCost,
                    onUpgradeSkill,
                    onRemoveSkill
                  );

                  return <SkillsPanelMobileCard key={item.skill.Id} {...props} />;
                })
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipPrimitive.Provider>
  );
};

// Helper function to extract prop logic outside the render loop for cleanliness
const getCommonProps = (
  item,
  activeSkills,
  aptitudes,
  efficiencyRankings,
  optimalSkillIds,
  isLimiterActive,
  getIconUrl,
  setHoveredUpgradeId,
  hoveredUpgradeId,
  // Handlers
  onToggleSkill,
  allSkills,
  onToggleFastLearner,
  onLevelChange,
  onToggleIgnoreCost,
  onUpgradeSkill,
  onRemoveSkill
) => {
  const skill = item.skill;
  const baseCost = skill.cost !== undefined && skill.cost !== null ? skill.cost : 0;
  const calculatedCost = calculateSkillCost(baseCost, item.level, item.fastLearner);

  const { adjustedScore, originalScore, multiplier, aptitude, rank, isCalculated } = calculateAdjustedScore(skill, aptitudes);

  // Check if the upgrade for this skill is already active
  const isUpgradeActive = skill.upgrade_id && activeSkills.some(s => parseInt(s.skill.Id_skill, 10) === parseInt(skill.upgrade_id, 10));

  // Efficiency Rank
  const skillId = parseInt(skill.Id_skill || skill.Id, 10);
  const efficiencyRank = efficiencyRankings ? efficiencyRankings.get(skillId) : null;

  // Limiter Logic
  const isOptimal = optimalSkillIds ? optimalSkillIds.has(skillId) : false;

  // Smart Highlight Logic
  // FIX: User requested strict visual feedback based on toggle state. 
  // Old logic (item.active || (isLimiterActive && isOptimal)) forced 'active' look on optimal skills even if unchecked.
  const shouldHighlight = item.active;

  // Dynamic Ranking Logic
  const showRank = efficiencyRank && (!isLimiterActive || isOptimal) && !item.active;

  return {
    item,
    skill,
    calculatedCost,
    adjustedScore,
    originalScore,
    multiplier,
    aptitude,
    rank,
    isCalculated,
    isUpgradeActive,
    efficiencyRank,
    isOptimal,
    shouldHighlight,
    showRank,
    onToggleSkill,
    getIconUrl,
    allSkills,
    onToggleFastLearner,
    onLevelChange,
    onToggleIgnoreCost,
    onUpgradeSkill,
    onRemoveSkill,
    setHoveredUpgradeId,
    hoveredUpgradeId
  };
};

export default SkillsPanel;
// Revisado: 2025-12-18 [I18n Scan]
